import { useMemo, useState } from 'react'
import { CloudCog, Link2, Plug, PlugZap, RefreshCw, Satellite, Unlink, Waves } from 'lucide-react'
import { Button, Divider, Metric } from '../ui/Panels'
import { StateBadge } from '../ui/State'
import { fmtClock } from '../../lib/format'
import type { Geofence, NebosvodStatus } from '../../types'

/* ============================================================================
 * ИНТЕГРАЦИЯ С СИСТЕМОЙ «НЕБОСВОД UTM»
 *
 * Панель показывает:
 *   • статус подключения к внешней системе;
 *   • синхронизацию геозон (обмен районами ограничений);
 *   • поступающую телеметрию.
 *
 * Формат обмена данными — ГОСТ Р 71934-2025, координаты WGS-84.
 * ========================================================================= */

/** Начальное состояние интеграции: до первого опроса считаем, что канал не подтверждён */
export const INITIAL_NEBIOSVOD: NebosvodStatus = {
  connected: false,
  lastSync: undefined,
  geozonesSynced: 0,
  telemetryRateHz: undefined,
  lastError: undefined,
}

export function NebosvodPanel({
  status,
  geofences,
  onSync,
  onToggle,
  syncing,
}: {
  status: NebosvodStatus
  geofences: Geofence[]
  onSync: () => void
  onToggle: (connect: boolean) => void
  syncing: boolean
}) {
  const [lastErrorLocal, setLastErrorLocal] = useState<string | null>(null)

  const state = status.connected ? 'approved' : 'critical-error'

  // Сколько геозон ожидают выгрузки — считаем по полученным с сервера
  const pending = useMemo(() => geofences.length, [geofences])

  return (
    <div className="space-y-2 p-2.5">
      {/* Статус подключения */}
      <div className={`rounded-md border px-2.5 py-2 ut-stated ut-state-${state}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold">
            <CloudCog size={12} aria-hidden />
            «Небосвод UTM»
          </div>
          <StateBadge state={state}>
            {status.connected ? 'Подключено' : 'Нет связи'}
          </StateBadge>
        </div>
        <div className="ut-metric mt-1.5 flex flex-wrap gap-x-3 text-[10px] text-ink-muted">
          <span>Канал: {status.connected ? 'HTTPS / ГОСТ Р 71934-2025' : '—'}</span>
          <span>Последняя синхронизация: {fmtClock(status.lastSync)}</span>
        </div>
        {status.lastError && (
          <div className="mt-1 text-[10px] text-critical">
            Ошибка: {status.lastError}
          </div>
        )}
      </div>

      {/* Синхронизация геозон */}
      <Divider label="Синхронизация геозон" />

      <div className="grid grid-cols-2 gap-2">
        <Metric
          label="Синхронизировано"
          value={status.geozonesSynced}
          icon={Waves}
          state={status.geozonesSynced > 0 ? 'approved' : 'pending'}
        />
        <Metric label="Ожидают выгрузки" value={pending} icon={RefreshCw} state={pending > 0 ? 'warning' : 'approved'} />
      </div>

      {/* Геозоны, полученные с консольного сервера */}
      <div className="max-h-40 overflow-auto rounded-md border border-line">
        {geofences.length === 0 ? (
          <div className="px-2 py-3 text-center text-[10px] text-ink-muted">
            Геозоны не загружены
          </div>
        ) : (
          <table className="ut-table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Тип</th>
                <th>Вершин</th>
              </tr>
            </thead>
            <tbody>
              {geofences.map((f, i) => (
                <tr key={String(f.id ?? i)}>
                  <td className="truncate">{f.name ?? `Геозона ${i + 1}`}</td>
                  <td className="text-[10px] text-ink-muted">{f.kind ?? '—'}</td>
                  <td className="ut-metric">{f.polygon?.length ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Телеметрия интеграции */}
      <Divider label="Телеметрия" />

      <div className="grid grid-cols-2 gap-2">
        <Metric
          label="Частота данных"
          value={status.telemetryRateHz !== undefined ? `${status.telemetryRateHz}` : '—'}
          unit="Гц"
          icon={Satellite}
          state={status.telemetryRateHz ? 'approved' : 'pending'}
        />
        <Metric
          label="Канал"
          value={status.connected ? ' активен' : ' неактивен'}
          icon={Link2}
          state={status.connected ? 'approved' : 'pending'}
        />
      </div>

      {/* Управление */}
      <div className="grid grid-cols-2 gap-1.5">
        <Button
          size="sm"
          icon={RefreshCw}
          disabled={!status.connected || syncing}
          onClick={() => {
            setLastErrorLocal(null)
            onSync()
          }}
        >
          {syncing ? 'Синхронизация…' : 'Синхронизировать'}
        </Button>
        {status.connected ? (
          <Button size="sm" icon={Unlink} onClick={() => onToggle(false)}>
            Отключить
          </Button>
        ) : (
          <Button size="sm" variant="primary" icon={PlugZap} onClick={() => onToggle(true)}>
            Подключить
          </Button>
        )}
      </div>

      {lastErrorLocal && (
        <div className="ut-stated ut-state-critical-error rounded-md border px-2 py-1 text-[10px]">
          {lastErrorLocal}
        </div>
      )}

      <div className="flex items-start gap-1.5 rounded-md border border-line px-2 py-1.5 text-[10px] leading-snug text-ink-muted">
        <Plug size={11} className="mt-0.5 shrink-0" aria-hidden />
        Обмен районами и телеметрией выполняется по ГОСТ Р 71934-2025 в СК WGS-84.
        При отсутствии связи консоль продолжает работу автономно.
      </div>
    </div>
  )
}
